import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import { API_BASE_URL } from '../config/api';
import { api } from '../services/api';
import { useAdvertiser } from '../contexts/AdvertiserContext';
import ChooseClientPlaceholder from './ChooseClientPlaceholder';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../i18n';
import '../styles/creative-upload.css';

interface ValidationError {
  code: string;
  message: string;
  severity: 'error' | 'warning';
}

type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
  type: 'IMAGE' | 'HTML5' | 'VIDEO';
  dimensions?: { width: number; height: number };
  clickUrl: string;
  trackingCode: string;
  validationErrors: ValidationError[];
  // Upload state
  uploadStatus: UploadStatus;
  uploadProgress: number;
  uploadError?: string;
}

const MAX_IMAGE_SIZE = 750 * 1024; // 750 KB
const MAX_HTML5_SIZE = 750 * 1024; // 750 KB initial load
// 100 MB — the hard edge limit (CloudFlare request-body cap, and nginx
// client_max_body_size). Bigger files are rejected before reaching the server,
// so we stop them here with a clear message instead of an opaque edge 413.
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100 MB

// Sanitize filename: transliterate Cyrillic and remove special characters
// Inline dimension input component for file cards
const DimensionInputs: React.FC<{
  fileId: string;
  initialWidth?: number;
  initialHeight?: number;
  onUpdate: (id: string, width: number | undefined, height: number | undefined) => void;
}> = ({ fileId, initialWidth, initialHeight, onUpdate }) => {
  const [width, setWidth] = useState(initialWidth?.toString() || '');
  const [height, setHeight] = useState(initialHeight?.toString() || '');

  const saveChanges = () => {
    const w = width ? parseInt(width) : undefined;
    const h = height ? parseInt(height) : undefined;
    onUpdate(fileId, w, h);
  };

  return (
    <div className="file-card-dimensions-input" onClick={(e) => e.stopPropagation()}>
      <input
        type="number"
        placeholder="W"
        className={`dim-input ${width && height ? 'filled' : ''}`}
        value={width}
        onChange={(e) => setWidth(e.target.value)}
        onBlur={saveChanges}
      />
      <span>×</span>
      <input
        type="number"
        placeholder="H"
        className={`dim-input ${width && height ? 'filled' : ''}`}
        value={height}
        onChange={(e) => setHeight(e.target.value)}
        onBlur={saveChanges}
      />
    </div>
  );
};

// Validation functions
const validateImage = (file: File): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Check file size
  if (file.size > MAX_IMAGE_SIZE) {
    errors.push({
      code: 'FILE_SIZE_EXCEEDED',
      message: i18n.t('creatives.errFileSizeKb', {
        size: (file.size / 1024).toFixed(0), limit: MAX_IMAGE_SIZE / 1024 }),
      severity: 'error'
    });
  }

  return errors;
};

const validateHtml5 = async (file: File): Promise<ValidationError[]> => {
  const errors: ValidationError[] = [];

  // Check file size
  if (file.size > MAX_HTML5_SIZE) {
    errors.push({
      code: 'FILE_SIZE_EXCEEDED',
      message: i18n.t('creatives.errFileSizeKb', {
        size: (file.size / 1024).toFixed(0), limit: MAX_HTML5_SIZE / 1024 }),
      severity: 'error'
    });
  }

  // For ZIP files, validate contents
  if (file.name.toLowerCase().endsWith('.zip')) {
    try {
      const zip = await JSZip.loadAsync(file);
      const fileNames = Object.keys(zip.files).filter(f => !f.startsWith('__MACOSX'));

      // Check max files
      if (fileNames.length > 100) {
        errors.push({
          code: 'TOO_MANY_FILES',
          message: i18n.t('creatives.errTooManyFiles', { count: fileNames.length, max: 100 }),
          severity: 'error'
        });
      }

      // Check for index.html
      const hasIndex = fileNames.some(f =>
        f.toLowerCase() === 'index.html' || f.toLowerCase().endsWith('/index.html')
      );
      if (!hasIndex) {
        errors.push({
          code: 'PRIMARY_HTML_MISSING',
          message: i18n.t('creatives.errNoIndex'),
          severity: 'error'
        });
      }

      // Find and analyze HTML files for clickTag
      const htmlFiles = fileNames.filter(f => f.toLowerCase().endsWith('.html'));
      let hasClickTag = false;
      let hasHardcodedClickTag = false;

      for (const htmlFile of htmlFiles) {
        const content = await zip.files[htmlFile].async('string');

        // Check for clickTag
        if (/var\s+clickTag\s*=/i.test(content) || /window\.clickTag/i.test(content)) {
          hasClickTag = true;
        }

        // Check for hardcoded clickTag (URL directly in href instead of variable)
        if (/onclick\s*=\s*["']window\.open\s*\(\s*["']https?:\/\//i.test(content)) {
          hasHardcodedClickTag = true;
        }
      }

      if (!hasClickTag) {
        errors.push({
          code: 'CLICK_TAG_MISSING',
          message: i18n.t('creatives.errNoClickTag'),
          severity: 'error'
        });
      }

      if (hasHardcodedClickTag) {
        errors.push({
          code: 'CLICK_TAG_HARD_CODED',
          message: i18n.t('creatives.errHardcodedClickTag'),
          severity: 'error'
        });
      }

    } catch (e) {
      errors.push({
        code: 'ZIP_INVALID',
        message: i18n.t('creatives.errZipInvalid'),
        severity: 'error'
      });
    }
  }

  return errors;
};

const validateVideo = (file: File): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (file.size > MAX_VIDEO_SIZE) {
    errors.push({
      code: 'FILE_SIZE_EXCEEDED',
      message: i18n.t('creatives.errFileSizeMb', {
        size: (file.size / (1024 * 1024)).toFixed(0), limit: 100 }),
      severity: 'error'
    });
  }

  return errors;
};

const sanitizeFilename = (filename: string): string => {
  const cyrillicMap: { [key: string]: string } = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
    'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
    'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
    'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Е': 'E', 'Ё': 'Yo',
    'Ж': 'Zh', 'З': 'Z', 'И': 'I', 'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M',
    'Н': 'N', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T', 'У': 'U',
    'Ф': 'F', 'Х': 'H', 'Ц': 'Ts', 'Ч': 'Ch', 'Ш': 'Sh', 'Щ': 'Sch',
    'Ъ': '', 'Ы': 'Y', 'Ь': '', 'Э': 'E', 'Ю': 'Yu', 'Я': 'Ya',
  };

  // Split name and extension
  const lastDot = filename.lastIndexOf('.');
  const name = lastDot > 0 ? filename.slice(0, lastDot) : filename;
  const ext = lastDot > 0 ? filename.slice(lastDot) : '';

  // Transliterate Cyrillic
  let sanitized = name.split('').map(char => cyrillicMap[char] ?? char).join('');

  // Replace spaces with underscores, remove special characters (keep only alphanumeric, dash, underscore)
  sanitized = sanitized
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_\-]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  return sanitized + ext.toLowerCase();
};

const CreativeUploadPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [editMode, setEditMode] = useState<'batch' | 'individual'>('batch');
  const [batchClickUrl, setBatchClickUrl] = useState('');
  const [batchTrackingCode, setBatchTrackingCode] = useState('');
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  // Advertiser selection from global context
  const { selectedAdvertiser, isAllClients, isLoading: advertisersLoading } = useAdvertiser();
  const selectedAdvertiserId = selectedAdvertiser?.advertiser_id || '';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const dragCounter = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Redirect to creatives list when all uploads complete successfully
  useEffect(() => {
    if (uploadComplete && files.length > 0) {
      const allSuccess = files.every(f => f.uploadStatus === 'success');
      if (allSuccess) {
        // Small delay to show success state
        const timer = setTimeout(() => {
          navigate('/creatives');
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [uploadComplete, files, navigate]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const isImageFile = (file: File): boolean => {
    if (file.type.startsWith('image/')) return true;
    const ext = file.name.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext || '');
  };

  const isVideoFile = (file: File): boolean => {
    if (file.type.startsWith('video/')) return true;
    const ext = file.name.toLowerCase().split('.').pop();
    return ['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext || '');
  };

  const getFileType = (file: File): 'IMAGE' | 'HTML5' | 'VIDEO' => {
    if (isImageFile(file)) return 'IMAGE';
    if (isVideoFile(file)) return 'VIDEO';
    const ext = file.name.toLowerCase().split('.').pop();
    if (['html', 'zip'].includes(ext || '')) return 'HTML5';
    return 'IMAGE';
  };

  const getImageDimensions = (file: File): Promise<{ width: number; height: number } | undefined> => {
    return new Promise((resolve) => {
      if (!isImageFile(file)) {
        resolve(undefined);
        return;
      }
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => resolve(undefined);
      img.src = URL.createObjectURL(file);
    });
  };

  const parseDimensionsFromFilename = (filename: string): { width: number; height: number } | undefined => {
    // Try patterns like: 300x250, 300_250, 300-250
    const match = filename.match(/(\d{2,4})[x_\-](\d{2,4})/i);
    if (match) {
      const width = parseInt(match[1]);
      const height = parseInt(match[2]);
      // Sanity check: dimensions should be reasonable ad sizes
      if (width >= 50 && width <= 2000 && height >= 50 && height <= 2000) {
        return { width, height };
      }
    }
    return undefined;
  };

  const parseDimensionsFromHtml = (content: string): { width: number; height: number } | undefined => {
    // Parse <meta name="ad.size" content="width=300,height=250">
    const metaMatch = content.match(/<meta[^>]*name=["']ad\.size["'][^>]*content=["']([^"']+)["']/i);
    if (metaMatch) {
      const sizeStr = metaMatch[1];
      const widthMatch = sizeStr.match(/width=(\d+)/i);
      const heightMatch = sizeStr.match(/height=(\d+)/i);
      if (widthMatch && heightMatch) {
        return { width: parseInt(widthMatch[1]), height: parseInt(heightMatch[1]) };
      }
    }
    // Try parsing width/height from style or inline
    const widthMatch = content.match(/width[:\s]*(\d+)px/i);
    const heightMatch = content.match(/height[:\s]*(\d+)px/i);
    if (widthMatch && heightMatch) {
      return { width: parseInt(widthMatch[1]), height: parseInt(heightMatch[1]) };
    }
    return undefined;
  };

  const getHtml5Dimensions = async (file: File): Promise<{ width: number; height: number } | undefined> => {
    const fileName = file.name.toLowerCase();

    // Handle .html files
    if (fileName.endsWith('.html')) {
      // Try filename first
      const fromFilename = parseDimensionsFromFilename(file.name);
      if (fromFilename) return fromFilename;

      // Then parse content
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          resolve(content ? parseDimensionsFromHtml(content) : undefined);
        };
        reader.onerror = () => resolve(undefined);
        reader.readAsText(file);
      });
    }

    // Handle .zip files
    if (fileName.endsWith('.zip')) {
      // Try filename first (e.g., banner_300x250.zip)
      const fromFilename = parseDimensionsFromFilename(file.name);
      if (fromFilename) return fromFilename;

      // Then try parsing zip contents
      try {
        const zip = await JSZip.loadAsync(file);
        // Look for index.html or any .html file
        const htmlFiles = Object.keys(zip.files).filter(name =>
          name.toLowerCase().endsWith('.html') && !name.startsWith('__MACOSX')
        );
        const mainHtml = htmlFiles.find(f => f.toLowerCase().includes('index')) || htmlFiles[0];

        if (mainHtml) {
          const content = await zip.files[mainHtml].async('string');
          return parseDimensionsFromHtml(content);
        }
      } catch (e) {
        console.error('Failed to parse zip:', e);
      }
    }

    return undefined;
  };

  const getVideoThumbnail = (file: File): Promise<{ preview: string; dimensions?: { width: number; height: number } }> => {
    return new Promise((resolve) => {
      if (!isVideoFile(file)) {
        resolve({ preview: '' });
        return;
      }

      const video = document.createElement('video');
      // preload='auto' forces Safari to decode buffered frames, which is
      // required for canvas drawImage to actually paint a frame.
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      // Safari refuses to decode video that isn't attached to the document
      // for first-frame canvas extraction. Hide it and append.
      video.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
      document.body.appendChild(video);

      const url = URL.createObjectURL(file);
      let settled = false;
      const cleanup = () => {
        try { URL.revokeObjectURL(url); } catch {}
        try { video.remove(); } catch {}
      };
      const finish = (result: { preview: string; dimensions?: { width: number; height: number } }) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(result);
      };

      // Hard timeout so Safari can't strand the upload UI.
      const timeoutId = window.setTimeout(() => {
        const dims = video.videoWidth && video.videoHeight
          ? { width: video.videoWidth, height: video.videoHeight }
          : undefined;
        finish({ preview: '', dimensions: dims });
      }, 6000);

      const drawNow = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          if (ctx && canvas.width && canvas.height) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
            // Detect a fully-black canvas (Safari sometimes returns one
            // before the decoder is ready) and treat as failure so we
            // can retry on the next animation frame.
            const isBlack = thumbnail.length < 3500;
            if (!isBlack) {
              window.clearTimeout(timeoutId);
              finish({
                preview: thumbnail,
                dimensions: { width: video.videoWidth, height: video.videoHeight },
              });
              return true;
            }
          }
        } catch {}
        return false;
      };

      const captureWithRetry = () => {
        // Try several animation frames — Safari often needs a tick or two
        // after `seeked` before the frame is actually decoded into the
        // video element.
        let attempts = 0;
        const tryDraw = () => {
          if (settled) return;
          if (drawNow()) return;
          attempts += 1;
          if (attempts < 30) {
            requestAnimationFrame(tryDraw);
          }
        };
        requestAnimationFrame(tryDraw);
      };

      // Prefer requestVideoFrameCallback — Safari 15.4+ has it and it
      // fires only when a real frame is available, which dodges the
      // "canvas is black" race.
      const v = video as HTMLVideoElement & {
        requestVideoFrameCallback?: (cb: () => void) => number;
      };

      video.onloadedmetadata = () => {
        const target = Math.min(0.5, (video.duration || 1) * 0.1);
        try {
          video.currentTime = target;
        } catch {
          captureWithRetry();
        }
      };

      const onSeeked = () => {
        if (typeof v.requestVideoFrameCallback === 'function') {
          v.requestVideoFrameCallback(() => {
            if (!drawNow()) captureWithRetry();
          });
        } else {
          captureWithRetry();
        }
      };

      video.onseeked = onSeeked;
      video.onloadeddata = () => {
        if (!settled) onSeeked();
      };
      video.onerror = () => finish({ preview: '' });

      video.src = url;
      // Kick the loader.
      try { video.load(); } catch {}
    });
  };

  // Allowed file extensions
  const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  const ALLOWED_VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov'];
  const ALLOWED_HTML5_EXTENSIONS = ['zip', 'html'];

  const isAllowedFile = (file: File): boolean => {
    const ext = file.name.toLowerCase().split('.').pop() || '';
    return [
      ...ALLOWED_IMAGE_EXTENSIONS,
      ...ALLOWED_VIDEO_EXTENSIONS,
      ...ALLOWED_HTML5_EXTENSIONS
    ].includes(ext);
  };

  const processFiles = useCallback(async (fileList: FileList | File[]) => {
    const newFiles: UploadedFile[] = [];

    for (const file of Array.from(fileList)) {
      // Skip unsupported file formats silently
      if (!isAllowedFile(file)) {
        continue;
      }

      const type = getFileType(file);
      let preview = '';
      let dimensions: { width: number; height: number } | undefined;
      let validationErrors: ValidationError[] = [];

      if (isImageFile(file)) {
        preview = URL.createObjectURL(file);
        dimensions = await getImageDimensions(file);
        validationErrors = validateImage(file);
      } else if (isVideoFile(file)) {
        const videoData = await getVideoThumbnail(file);
        preview = videoData.preview;
        dimensions = videoData.dimensions;
        validationErrors = validateVideo(file);
      } else if (type === 'HTML5') {
        dimensions = await getHtml5Dimensions(file);
        validationErrors = await validateHtml5(file);
      }

      newFiles.push({
        id: generateId(),
        file,
        preview,
        name: sanitizeFilename(file.name),
        size: file.size,
        type,
        dimensions,
        clickUrl: editMode === 'batch' ? batchClickUrl : '',
        trackingCode: editMode === 'batch' ? batchTrackingCode : '',
        validationErrors,
        uploadStatus: 'pending',
        uploadProgress: 0,
      });
    }

    setFiles(prev => [...prev, ...newFiles]);
  }, [editMode, batchClickUrl, batchTrackingCode]);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items?.length) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    // Prefer dataTransfer.files (Chrome/FF). Fall back to dataTransfer.items
    // because Safari sometimes only populates items for certain file types.
    if (e.dataTransfer.files?.length) {
      processFiles(e.dataTransfer.files);
      return;
    }
    if (e.dataTransfer.items?.length) {
      const files: File[] = [];
      for (const item of Array.from(e.dataTransfer.items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) processFiles(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      processFiles(e.target.files);
    }
  };

  const removeFile = (id: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== id);
    });
    if (selectedFileId === id) setSelectedFileId(null);
  };

  const updateFileField = (id: string, field: 'clickUrl' | 'trackingCode', value: string) => {
    setFiles(prev => prev.map(f =>
      f.id === id ? { ...f, [field]: value } : f
    ));
  };

  const updateFileDimensions = (id: string, width: number | undefined, height: number | undefined) => {
    setFiles(prev => prev.map(f =>
      f.id === id ? { ...f, dimensions: width && height ? { width, height } : undefined } : f
    ));
  };

  const filesWithoutDimensions = files.filter(f => !f.dimensions);
  const filesWithoutClickUrl = files.filter(f => !f.clickUrl);
  const filesWithErrors = files.filter(f => f.validationErrors.some(e => e.severity === 'error'));
  const canUpload = files.length > 0 &&
    selectedAdvertiserId &&
    filesWithoutDimensions.length === 0 &&
    filesWithoutClickUrl.length === 0 &&
    filesWithErrors.length === 0;

  // Upload progress calculations
  const uploadedCount = files.filter(f => f.uploadStatus === 'success').length;
  const failedCount = files.filter(f => f.uploadStatus === 'error').length;
  const overallProgress = files.length > 0
    ? Math.round(files.reduce((sum, f) => sum + f.uploadProgress, 0) / files.length)
    : 0;

  // Upload a single file to storage
  const uploadSingleFile = async (fileData: UploadedFile, signal: AbortSignal): Promise<void> => {
    // Update status to uploading
    setFiles(prev => prev.map(f =>
      f.id === fileData.id ? { ...f, uploadStatus: 'uploading' as UploadStatus, uploadProgress: 0 } : f
    ));

    try {
      const formData = new FormData();
      formData.append('file', fileData.file);
      formData.append('advertiser_id', selectedAdvertiserId);
      formData.append('name', fileData.name);
      formData.append('type', fileData.type);
      formData.append('click_url', fileData.clickUrl || '');
      if (fileData.trackingCode) {
        formData.append('tracking_code', fileData.trackingCode);
      }
      if (fileData.dimensions) {
        formData.append('width', fileData.dimensions.width.toString());
        formData.append('height', fileData.dimensions.height.toString());
      }

      // Simulated progress updates (XHR would give real progress)
      const progressInterval = setInterval(() => {
        setFiles(prev => prev.map(f => {
          if (f.id === fileData.id && f.uploadStatus === 'uploading' && f.uploadProgress < 90) {
            return { ...f, uploadProgress: f.uploadProgress + 10 };
          }
          return f;
        }));
      }, 200);

      // Raw fetch (we need streaming multipart upload, can't go through
      // the axios api wrapper). Implement the same 401-refresh-and-retry
      // dance here so a long upload doesn't fail just because the access
      // token expired between batch start and this request.
      const doFetch = async (token: string | null) => fetch(`${API_BASE_URL}/api/creatives/upload`, {
        method: 'POST',
        body: formData,
        signal,
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });

      let token = localStorage.getItem('access_token');
      let response = await doFetch(token);

      if (response.status === 401) {
        // Try to refresh and retry once.
        const refreshResp = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (refreshResp.ok) {
          const data = await refreshResp.json();
          token = data.access_token;
          if (token) {
            localStorage.setItem('access_token', token);
            response = await doFetch(token);
          }
        }
      }

      clearInterval(progressInterval);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: i18n.t('creatives.uploadFailedShort') }));
        throw new Error(error.detail || i18n.t('creatives.uploadFailed'));
      }

      // Success
      setFiles(prev => prev.map(f =>
        f.id === fileData.id ? { ...f, uploadStatus: 'success' as UploadStatus, uploadProgress: 100 } : f
      ));
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setFiles(prev => prev.map(f =>
          f.id === fileData.id ? { ...f, uploadStatus: 'pending' as UploadStatus, uploadProgress: 0 } : f
        ));
        return;
      }
      setFiles(prev => prev.map(f =>
        f.id === fileData.id ? {
          ...f,
          uploadStatus: 'error' as UploadStatus,
          uploadProgress: 0,
          uploadError: error.message || 'Upload failed'
        } : f
      ));
    }
  };

  // Start uploading all files in parallel
  const startUpload = async () => {
    if (!canUpload || isUploading) return;

    setIsUploading(true);
    setUploadComplete(false);
    abortControllerRef.current = new AbortController();

    // Reset all files to pending
    setFiles(prev => prev.map(f => ({
      ...f,
      uploadStatus: 'pending' as UploadStatus,
      uploadProgress: 0,
      uploadError: undefined,
    })));

    // Upload all files in parallel
    const uploadPromises = files.map(f => uploadSingleFile(f, abortControllerRef.current!.signal));
    await Promise.allSettled(uploadPromises);

    setIsUploading(false);
    setUploadComplete(true);
    abortControllerRef.current = null;
  };

  // Cancel all uploads
  const cancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsUploading(false);
  };

  const applyBatchToAll = () => {
    setFiles(prev => prev.map(f => ({
      ...f,
      clickUrl: batchClickUrl,
      trackingCode: batchTrackingCode,
    })));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'IMAGE':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
          </svg>
        );
      case 'VIDEO':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z"/>
          </svg>
        );
      case 'HTML5':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const selectedFile = files.find(f => f.id === selectedFileId);

  if (isAllClients) return <ChooseClientPlaceholder pageKey="nav.creativeUpload" />;

  return (
    <div className="upload-page">
      {/* Header */}
      <header className="upload-header">
        <div className="upload-header-content">
          <div className="upload-header-text">
            <h1>{t('creatives.uploadTitle')}</h1>
            <p>
              {isUploading
                ? t('creatives.uploadingN', { done: uploadedCount, total: files.length })
                : uploadComplete
                ? t('creatives.uploadDone', { ok: uploadedCount, failed: failedCount })
                : t('creatives.uploadSubtitle')
              }
            </p>
          </div>
          <div className="upload-header-actions">
            {isUploading ? (
              <>
                <button className="btn-cancel" onClick={cancelUpload}>
                  {t('creatives.cancelUpload')}
                </button>
                <button className="btn-primary uploading" disabled>
                  <div className="upload-btn-progress" style={{ width: `${overallProgress}%` }} />
                  <span className="upload-btn-text">
                    <span className="spinner" />
                    {overallProgress}%
                  </span>
                </button>
              </>
            ) : (
              <>
                {!canUpload && files.length > 0 && (
                  <span style={{ color: '#e5484d', fontSize: 13, fontWeight: 500, alignSelf: 'center', marginRight: 4 }}>
                    {filesWithoutDimensions.length > 0
                      ? t('creatives.needDimensions', { count: filesWithoutDimensions.length })
                      : filesWithoutClickUrl.length > 0
                      ? t('creatives.needClickUrlShort')
                      : filesWithErrors.length > 0
                      ? t('creatives.haveErrors', { count: filesWithErrors.length })
                      : ''}
                  </span>
                )}
                <button className="btn-secondary" onClick={() => window.history.back()}>
                  {t('creatives.cancel')}
                </button>
                <button
                  className="btn-primary"
                  disabled={!canUpload}
                  onClick={startUpload}
                  title={
                    filesWithoutDimensions.length > 0
                      ? t('creatives.needDimensions', { count: filesWithoutDimensions.length })
                      : filesWithoutClickUrl.length > 0
                      ? t('creatives.needClickUrl', { count: filesWithoutClickUrl.length })
                      : filesWithErrors.length > 0
                      ? t('creatives.haveValidationErrors', { count: filesWithErrors.length })
                      : ''
                  }
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  {t('creatives.uploadBtn')} {files.length > 0 && `(${files.length})`}
                </button>
              </>
            )}
          </div>
        </div>
        {/* Progress bar */}
        {isUploading && (
          <div className="upload-progress-bar" style={{ width: `${overallProgress}%` }} />
        )}
      </header>

      <div className="upload-content">
        {/* Left Panel - Drop Zone & File List */}
        <div className="upload-left-panel">
          {/* Drop Zone */}
          <div
            ref={dropZoneRef}
            className={`drop-zone ${isDragging ? 'dragging' : ''} ${files.length > 0 ? 'has-files' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              /* No `accept` filter — Safari is overly strict and silently
                 rejects mp4 files whose `file.type` comes back empty
                 after drag-from-Finder. We validate by extension in JS
                 (isAllowedFile) which works in all browsers. */
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <div className="drop-zone-content">
              <div className="drop-zone-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 16V4m0 0l-4 4m4-4l4 4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 16v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="drop-zone-text">
                <span className="drop-zone-title">
                  {t(isDragging ? 'creatives.dropHere' : 'creatives.dragDrop')}
                </span>
                <span className="drop-zone-subtitle">
                  <Trans i18nKey="creatives.orBrowse"
                    components={{ b: <span className="drop-zone-link" /> }} />
                </span>
              </div>
              <div className="drop-zone-formats">
                <span>PNG</span>
                <span>JPG</span>
                <span>GIF</span>
                <span>MP4</span>
                <span>HTML5</span>
              </div>
            </div>

            {/* Animated border */}
            <div className="drop-zone-border"></div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="file-list">
              <div className="file-list-header">
                <h3>{t('creatives.nFilesSelected', { count: files.length })}</h3>
                <button className="btn-text-danger" onClick={() => setFiles([])}>
                  {t('creatives.clearAll')}
                </button>
              </div>

              {/* SVG gradient definition for progress circles */}
              <svg style={{ position: 'absolute', width: 0, height: 0 }}>
                <defs>
                  <linearGradient id="progress-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FF6B4A" />
                    <stop offset="100%" stopColor="#8E3A9A" />
                  </linearGradient>
                </defs>
              </svg>

              <div className="file-grid">
                {files.map((file, index) => {
                  const hasIssues = !file.dimensions || !file.clickUrl || file.validationErrors.length > 0;
                  const cardClasses = [
                    'file-card',
                    selectedFileId === file.id && 'selected',
                    file.uploadStatus === 'pending' && hasIssues && 'has-errors',
                    file.uploadStatus === 'uploading' && 'uploading',
                    file.uploadStatus === 'success' && 'upload-success',
                    file.uploadStatus === 'error' && 'upload-error',
                  ].filter(Boolean).join(' ');

                  // Calculate stroke offset for circular progress (circumference = 2 * PI * r = 126 for r=20)
                  const circumference = 126;
                  const strokeOffset = circumference - (file.uploadProgress / 100) * circumference;

                  return (
                    <div
                      key={file.id}
                      className={cardClasses}
                      onClick={() => editMode === 'individual' && file.uploadStatus === 'pending' && setSelectedFileId(file.id)}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="file-card-preview">
                        {file.preview ? (
                          <img src={file.preview} alt={file.name} />
                        ) : (
                          <div className={`file-card-placeholder ${file.type.toLowerCase()}`}>
                            {getTypeIcon(file.type)}
                          </div>
                        )}
                        <div className="file-card-type">
                          {getTypeIcon(file.type)}
                        </div>

                        {/* Remove button - hide during/after upload */}
                        {file.uploadStatus === 'pending' && (
                          <button
                            className="file-card-remove"
                            onClick={(e) => { e.stopPropagation(); removeFile(file.id); }}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                            </svg>
                          </button>
                        )}

                        {/* Upload progress overlay */}
                        {file.uploadStatus === 'uploading' && (
                          <div className="file-card-upload-overlay">
                            <div className="circular-progress">
                              <svg viewBox="0 0 48 48">
                                <circle className="progress-bg" cx="24" cy="24" r="20" />
                                <circle
                                  className="progress-fill"
                                  cx="24" cy="24" r="20"
                                  style={{ strokeDashoffset: strokeOffset }}
                                />
                              </svg>
                              <span className="progress-text">{file.uploadProgress}%</span>
                            </div>
                          </div>
                        )}

                        {/* Status badges container */}
                        <div className="file-card-badges">
                          {/* Success badge after upload */}
                          {file.uploadStatus === 'success' && (
                            <div className="file-badge success" title={t('creatives.uploadedOk')}>
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            </div>
                          )}
                          {/* Error badge after failed upload */}
                          {file.uploadStatus === 'error' && (
                            <div className="file-badge error" title={file.uploadError || t('creatives.uploadFailedShort')}>
                              <span>!</span>
                            </div>
                          )}
                          {/* Show validation badges only before upload */}
                          {file.uploadStatus === 'pending' && (
                            <>
                              {/* Error badge for validation errors */}
                              {file.validationErrors.length > 0 && (
                                <div className="file-badge error" title={t('creatives.validationErrors')}>
                                  <span>!</span>
                                </div>
                              )}
                              {/* Warning for missing dimensions */}
                              {!file.dimensions && (
                                <div className="file-badge warning" title={t('creatives.dimensionsRequired')}>
                                  <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                                  </svg>
                                </div>
                              )}
                              {/* Warning for missing click URL */}
                              {!file.clickUrl && (
                                <div className="file-badge link-missing" title={t('creatives.clickUrlRequired')}>
                                  <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                                  </svg>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    <div className="file-card-info">
                      <span className="file-card-name" title={file.name}>
                        {file.name}
                      </span>
                      <div className="file-card-meta-row">
                        <DimensionInputs
                          fileId={file.id}
                          initialWidth={file.dimensions?.width}
                          initialHeight={file.dimensions?.height}
                          onUpdate={updateFileDimensions}
                        />
                        <span className="file-card-size">{formatFileSize(file.size)}</span>
                      </div>
                    </div>
                    {/* Error tooltip on hover - shows all issues */}
                    {file.uploadStatus === 'pending' && (file.validationErrors.length > 0 || !file.dimensions || !file.clickUrl) && (
                      <div className="file-card-error-tooltip">
                        {file.validationErrors.map((error, i) => (
                          <div key={`err-${i}`} className="error-item">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                            </svg>
                            <span>{error.message}</span>
                          </div>
                        ))}
                        {!file.dimensions && (
                          <div className="error-item warning">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                            </svg>
                            <span>{t('creatives.dimensionsMissing')}</span>
                          </div>
                        )}
                        {!file.clickUrl && (
                          <div className="error-item warning">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                            </svg>
                            <span>{t('creatives.clickUrlMissing')}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Upload error tooltip */}
                    {file.uploadStatus === 'error' && file.uploadError && (
                      <div className="file-card-error-tooltip">
                        <div className="error-item">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                          </svg>
                          <span>{file.uploadError}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Settings */}
        <div className="upload-right-panel">
          <div className="settings-card">
            <div className="settings-header">
              <h2>{t('creatives.trackingSettings')}</h2>
              <p>{t('creatives.trackingSubtitleUpload')}</p>
            </div>

            {/* Mode Toggle */}
            <div className="mode-toggle">
              <button
                className={`mode-toggle-btn ${editMode === 'batch' ? 'active' : ''}`}
                onClick={() => setEditMode('batch')}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
                </svg>
                {t('creatives.applyToAll')}
              </button>
              <button
                className={`mode-toggle-btn ${editMode === 'individual' ? 'active' : ''}`}
                onClick={() => setEditMode('individual')}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3h8v8H3zm0 10h8v8H3zm10-10h8v8h-8zm0 10h8v8h-8z"/>
                </svg>
                {t('creatives.individual')}
              </button>
              <div className={`mode-toggle-indicator ${editMode}`}></div>
            </div>

            {/* Batch Mode */}
            {editMode === 'batch' && (
              <div className="settings-form batch-form">
                <div className="form-group">
                  <label>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                    </svg>
                    {t('creatives.clickUrl')} <span style={{ color: '#e5484d' }}>*</span>
                  </label>
                  <input
                    type="url"
                    placeholder="https://example.com/landing"
                    value={batchClickUrl}
                    onChange={(e) => setBatchClickUrl(e.target.value)}
                    style={!batchClickUrl.trim() ? { borderColor: '#e5484d', boxShadow: '0 0 0 3px rgba(229,72,77,0.12)' } : undefined}
                  />
                  <span className="form-hint" style={!batchClickUrl.trim() ? { color: '#e5484d' } : undefined}>
                    {t(!batchClickUrl.trim()
                      ? 'creatives.clickUrlRequiredHint'
                      : 'creatives.landingHintOne')}
                  </span>
                </div>

                {(() => {
                  const allVideo = files.length > 0 && files.every(f => f.type === 'VIDEO');
                  return (
                    <div className="form-group">
                      <label>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                        </svg>
                        {t(allVideo ? 'creatives.impressionTrackerUrl' : 'creatives.htmlTrackingCode')}
                      </label>
                      <textarea
                        placeholder={allVideo
                          ? 'https://tracker.example.com/imp'
                          : "<img src='https://tracker.example.com/pixel' />"}
                        value={batchTrackingCode}
                        onChange={(e) => setBatchTrackingCode(e.target.value)}
                        rows={allVideo ? 2 : 4}
                      />
                      <span className="form-hint">
                        {t(allVideo ? 'creatives.vastHint' : 'creatives.pixelHintOne')}
                      </span>
                    </div>
                  );
                })()}

                {files.length > 0 && (
                  <button className="btn-apply" onClick={applyBatchToAll}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    {t('creatives.applyToAllFiles', { count: files.length })}
                  </button>
                )}
              </div>
            )}

            {/* Individual Mode */}
            {editMode === 'individual' && (
              <div className="settings-form individual-form">
                {!selectedFile ? (
                  <div className="no-selection">
                    <div className="no-selection-icon">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"/>
                      </svg>
                    </div>
                    <p>{t('creatives.selectFileHint')}</p>
                  </div>
                ) : (
                  <>
                    <div className="selected-file-header">
                      <div className="selected-file-preview">
                        {selectedFile.preview ? (
                          <img src={selectedFile.preview} alt={selectedFile.name} />
                        ) : (
                          <div className={`file-placeholder ${selectedFile.type.toLowerCase()}`}>
                            {getTypeIcon(selectedFile.type)}
                          </div>
                        )}
                      </div>
                      <div className="selected-file-info">
                        <span className="selected-file-name">{selectedFile.name}</span>
                        <span className="selected-file-meta">
                          {selectedFile.type}
                          {selectedFile.dimensions && ` · ${selectedFile.dimensions.width}×${selectedFile.dimensions.height}`}
                        </span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                        </svg>
                        {t('creatives.clickUrl')} <span style={{ color: '#e5484d' }}>*</span>
                      </label>
                      <input
                        type="url"
                        placeholder="https://example.com/landing"
                        value={selectedFile.clickUrl}
                        onChange={(e) => updateFileField(selectedFile.id, 'clickUrl', e.target.value)}
                        style={!selectedFile.clickUrl.trim() ? { borderColor: '#e5484d', boxShadow: '0 0 0 3px rgba(229,72,77,0.12)' } : undefined}
                      />
                      {!selectedFile.clickUrl.trim() && (
                        <span className="form-hint" style={{ color: '#e5484d' }}>
                          {t('creatives.clickUrlRequiredHint')}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                        </svg>
                        {t(selectedFile.type === 'VIDEO' ? 'creatives.impressionTrackerUrl' : 'creatives.htmlTrackingCode')}
                      </label>
                      <textarea
                        placeholder={selectedFile.type === 'VIDEO'
                          ? 'https://tracker.example.com/imp'
                          : "<img src='https://tracker.example.com/pixel' />"}
                        value={selectedFile.trackingCode}
                        onChange={(e) => updateFileField(selectedFile.id, 'trackingCode', e.target.value)}
                        rows={selectedFile.type === 'VIDEO' ? 2 : 4}
                      />
                      {selectedFile.type === 'VIDEO' && (
                        <span className="form-hint">
                          {t('creatives.vastHint')}
                        </span>
                      )}
                    </div>

                    {/* Navigation between files */}
                    <div className="file-navigation">
                      <button
                        className="btn-nav"
                        disabled={files.findIndex(f => f.id === selectedFileId) === 0}
                        onClick={() => {
                          const idx = files.findIndex(f => f.id === selectedFileId);
                          if (idx > 0) setSelectedFileId(files[idx - 1].id);
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                        </svg>
                        {t('creatives.previous')}
                      </button>
                      <span className="file-counter">
                        {files.findIndex(f => f.id === selectedFileId) + 1} / {files.length}
                      </span>
                      <button
                        className="btn-nav"
                        disabled={files.findIndex(f => f.id === selectedFileId) === files.length - 1}
                        onClick={() => {
                          const idx = files.findIndex(f => f.id === selectedFileId);
                          if (idx < files.length - 1) setSelectedFileId(files[idx + 1].id);
                        }}
                      >
                        {t('creatives.next')}
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Summary Card */}
          {files.length > 0 && (
            <div className="summary-card">
              <h3>{t('creatives.uploadSummary')}</h3>
              <div className="summary-stats">
                <div className="summary-stat">
                  <span className="stat-value">{files.length}</span>
                  <span className="stat-label">{t('creatives.statFiles')}</span>
                </div>
                <div className="summary-stat">
                  <span className="stat-value">
                    {formatFileSize(files.reduce((sum, f) => sum + f.size, 0))}
                  </span>
                  <span className="stat-label">{t('creatives.statTotalSize')}</span>
                </div>
                <div className="summary-stat">
                  <span className="stat-value">
                    {files.filter(f => f.clickUrl || f.trackingCode).length}
                  </span>
                  <span className="stat-label">{t('creatives.statConfigured')}</span>
                </div>
              </div>
              <div className="summary-types">
                {['IMAGE', 'VIDEO', 'HTML5'].map(type => {
                  const count = files.filter(f => f.type === type).length;
                  if (count === 0) return null;
                  return (
                    <div key={type} className={`summary-type ${type.toLowerCase()}`}>
                      {getTypeIcon(type)}
                      <span>{t(`creatives.typeCount${type.charAt(0)}${type.slice(1).toLowerCase()}`, { count })}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreativeUploadPage;
